import { Alert, Button, Input, Space, Typography } from "antd";
import type { InstallPromotionRecord, InstallPromotionCommissionVo } from "@zhihaoscm/entity";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { INSTALL_PROMOTION_REOCRD_STATE } from "@/meta/logistics/installPromotionReocrd";
import { InstallPromotionCommissionList } from "@/views/pages/logistics/installPromotionRecord/InstallPromotionCommissionList";

export interface InstallPromotionRecordDetailPageProps {
    recordId?: number | string;
    loadRecord?: (id: number | string) => Promise<Partial<InstallPromotionRecord> | undefined>;
    loadCommission?: (params: { recordId: number }) => Promise<Partial<InstallPromotionCommissionVo>>;
    revokeRecord?: (id: number | string, revocationReason: string) => Promise<unknown>;
}

/** 复用 Vue 安装推广记录详情接口。 */
export async function loadInstallPromotionRecord(id: number | string) {
    const response = await request.service.get<Partial<InstallPromotionRecord>>(`/install/promotion/record/${id}`);
    return response.data;
}

/** 复用 Vue 安装推广佣金汇总接口。 */
export async function loadInstallPromotionCommission(params: { recordId: number }) {
    const response = await request.service.get<Partial<InstallPromotionCommissionVo>>(
        "/install-promotion-commission/compute",
        { params }
    );
    return response.data;
}

/** 复用 Vue 安装推广记录撤销接口。 */
export async function revokeInstallPromotionRecord(id: number | string, revocationReason: string) {
    const response = await request.service.get(`/install/promotion/record/revoke/activation/${id}`, {
        // 保持 Vue installPromotionRecordService.revoke 的查询参数契约。
        params: { revocationReason }
    });
    return response.data;
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatCustomer = (customer?: InstallPromotionRecord["promoter"]) =>
    customer
        ? `${customer.institutionName || customer.realName || DEFAULT_VALUE} | ${customer.mobile || DEFAULT_VALUE} | ${customer.code || DEFAULT_VALUE}`
        : DEFAULT_VALUE;
const formatAmount = (value?: number) => (value == null ? DEFAULT_VALUE : `${value}元`);

/** 物流管理-安装推广记录详情，覆盖 Vue 推广信息、撤销信息和佣金汇总。 */
export function InstallPromotionRecordDetailPage({
    recordId,
    loadRecord = loadInstallPromotionRecord,
    loadCommission = loadInstallPromotionCommission,
    revokeRecord = revokeInstallPromotionRecord
}: InstallPromotionRecordDetailPageProps) {
    const params = useParams<{ id: string }>();
    const navigate = useNavigate();
    const resolvedId = recordId ?? params.id ?? 0;
    const [record, setRecord] = useState<Partial<InstallPromotionRecord>>();
    const [commission, setCommission] = useState<Partial<InstallPromotionCommissionVo>>();
    const [revokeVisible, setRevokeVisible] = useState(false);
    const [revokeReason, setRevokeReason] = useState("");
    const [revokeError, setRevokeError] = useState("");
    const [revoking, setRevoking] = useState(false);
    const [actionFeedback, setActionFeedback] = useState<{ type: "success" | "error"; message: string }>();

    // 详情刷新同时更新记录和佣金汇总，撤销成功后复用该逻辑保持页面状态一致。
    const refreshDetail = useCallback(
        async (isDisposed: () => boolean = () => false) => {
            const result = await loadRecord(resolvedId);
            if (isDisposed()) return;
            setRecord(result);
            if (result?.id != null) {
                const commissionResult = await loadCommission({ recordId: Number(result.id) });
                if (!isDisposed()) setCommission(commissionResult);
            }
        },
        [loadCommission, loadRecord, resolvedId]
    );

    useEffect(() => {
        let disposed = false;
        void refreshDetail(() => disposed);
        return () => {
            disposed = true;
        };
    }, [refreshDetail]);

    const invalid = Number(record?.state) === INSTALL_PROMOTION_REOCRD_STATE.INVALID.code;
    const activated = Number(record?.state) === INSTALL_PROMOTION_REOCRD_STATE.ACTIVATED.code;

    const confirmRevoke = async () => {
        const reason = revokeReason.trim();
        if (!reason) {
            setRevokeError("请输入撤销理由");
            return;
        }
        setRevoking(true);
        try {
            await revokeRecord(resolvedId, reason);
            setRevokeVisible(false);
            setRevokeReason("");
            setRevokeError("");
            await refreshDetail();
            setActionFeedback({ type: "success", message: "已撤销激活" });
        } catch {
            // 撤销或成功后的详情刷新失败时保留弹窗，并给出明确失败反馈。
            setActionFeedback({ type: "error", message: "撤销激活失败" });
        } finally {
            setRevoking(false);
        }
    };

    return (
        <section aria-label="install-promotion-record-detail-page" className="flex min-h-full flex-col gap-4">
            {actionFeedback ? (
                <Alert
                    type={actionFeedback.type}
                    message={actionFeedback.message}
                    showIcon
                    closable
                    onClose={() => setActionFeedback(undefined)}
                />
            ) : null}
            <div className="section-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: record?.code || DEFAULT_VALUE }]}
                    rightInfos={[
                        ...(invalid ? [{ label: "失效时间", value: formatDate(record?.createdTime) }] : []),
                        { label: "创建时间", value: formatDate(record?.createdTime) }
                    ]}
                    stateDesc={INSTALL_PROMOTION_REOCRD_STATE.getIntroByCode(record?.state) || DEFAULT_VALUE}
                    title="安装推广记录详情"
                />
                <div className="section-title mt-6">推广信息</div>
                <ZhGrid className="mt-5" cols={4} gap={[30, 12]}>
                    <ZhBaseItem label="推介人账号" value={formatCustomer(record?.promoter)} />
                    <ZhBaseItem
                        label="船舶"
                        value={
                            record?.ship?.cnname
                                ? `${record.ship.cnname} / ${record.ship.name || DEFAULT_VALUE}`
                                : record?.ship?.name || DEFAULT_VALUE
                        }
                    />
                    <ZhBaseItem label="承运商账号" span={2} value={formatCustomer(record?.carrier)} />
                    <ZhBaseItem label="生效日期" value={formatDate(record?.createdTime)} />
                    <ZhBaseItem label="截止日期" value={formatDate(record?.deadline)} />
                </ZhGrid>
                {record?.revocationReason ? (
                    <>
                        <div className="section-title mt-6">撤销信息</div>
                        <ZhGrid className="mt-5" cols={1} gap={[30, 12]}>
                            <ZhBaseItem label="撤销人" value={record.revocationName || DEFAULT_VALUE} />
                            <ZhBaseItem label="理由" value={record.revocationReason} />
                        </ZhGrid>
                    </>
                ) : null}
                <div className="section-title mt-6">推广佣金记录</div>
                <ZhGrid className="mt-5" cols={4} gap={[30, 12]}>
                    <ZhBaseItem label="总佣金" value={formatAmount(commission?.totalAmount)} />
                    <ZhBaseItem label="已结算" value={formatAmount(commission?.settledAmount)} />
                    <ZhBaseItem label="未结算" value={formatAmount(commission?.notSettledAmount)} />
                </ZhGrid>
                {record?.code ? <InstallPromotionCommissionList code={record.code} /> : null}
                <Space className="operation-fixed-btn">
                    {activated ? (
                        <Button
                            aria-label="撤销"
                            type="primary"
                            onClick={() => {
                                setRevokeReason("");
                                setRevokeError("");
                                setRevokeVisible(true);
                            }}
                        >
                            撤销
                        </Button>
                    ) : null}
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </Space>
            </div>
            <ZhDialog
                title="撤销激活"
                open={revokeVisible}
                width={544}
                onCancel={() => setRevokeVisible(false)}
                footer={
                    <Space>
                        <Button disabled={revoking} onClick={() => setRevokeVisible(false)}>
                            取消
                        </Button>
                        <Button loading={revoking} type="primary" onClick={() => void confirmRevoke()}>
                            确定
                        </Button>
                    </Space>
                }
            >
                <Alert className="mb-4" message="是否撤销激活，撤销后此安装推广将失效" type="info" />
                <Input.TextArea
                    maxLength={200}
                    showCount
                    placeholder="请输入撤销理由"
                    value={revokeReason}
                    onChange={event => setRevokeReason(event.target.value)}
                />
                {revokeError ? <Typography.Text type="danger">{revokeError}</Typography.Text> : null}
            </ZhDialog>
        </section>
    );
}
